import { useMemo, useState } from 'react';

interface ContentRendererProps {
  content: string;
  slug?: string;
}

interface InfoBox {
  type: 'highlight' | 'warning' | 'tip' | 'checklist';
  icon: string;
  label: string;
  title: string;
  items: string[];
}

interface Step {
  number: number;
  title: string;
  description: string;
  icon: string;
  tips?: string;
}

interface StepGuide {
  title: string;
  color: 'cyan' | 'teal' | 'blue' | 'purple' | 'amber';
  steps: Step[];
}

interface FlowNodeOption {
  label: string;
  next: string;
}

interface FlowNode {
  id: string;
  type: 'question' | 'solution' | 'action';
  text: string;
  yesNext?: string;
  noNext?: string;
  options?: FlowNodeOption[];
  icon?: string;
}

interface DiagnosticFlowchart {
  category?: 'video' | 'audio' | 'network' | 'custom';
  title: string;
  icon: string;
  color: string;
  nodes: FlowNode[];
}

const boxStyles = {
  highlight: {
    container: 'bg-amber-50 border-l-4 border-amber-400',
    iconBg: 'bg-amber-100 text-amber-600',
    label: 'text-amber-700',
    title: 'text-amber-900',
    bullet: 'text-amber-400',
  },
  warning: {
    container: 'bg-red-50 border-l-4 border-red-400',
    iconBg: 'bg-red-100 text-red-600',
    label: 'text-red-700',
    title: 'text-red-900',
    bullet: 'text-red-400',
  },
  tip: {
    container: 'bg-emerald-50 border-l-4 border-emerald-400',
    iconBg: 'bg-emerald-100 text-emerald-600',
    label: 'text-emerald-700',
    title: 'text-emerald-900',
    bullet: 'text-emerald-400',
  },
  checklist: {
    container: 'bg-sky-50 border-l-4 border-sky-400',
    iconBg: 'bg-sky-100 text-sky-600',
    label: 'text-sky-700',
    title: 'text-sky-900',
    bullet: 'text-sky-400',
  },
};

const boxIcons = {
  highlight: 'ri-lightbulb-flash-line',
  warning: 'ri-error-warning-line',
  tip: 'ri-magic-line',
  checklist: 'ri-checkbox-circle-line',
};

const boxLabels = {
  highlight: '重要ポイント',
  warning: '注意',
  tip: 'プロのTips',
  checklist: 'チェックリスト',
};

function InfoBoxComponent({ box }: { box: InfoBox }) {
  const style = boxStyles[box.type];

  return (
    <div className={`${style.container} rounded-lg p-5 my-6`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 flex items-center justify-center rounded-full ${style.iconBg}`}>
          <i className={`${box.icon} text-lg`}></i>
        </div>
        <span className={`text-xs font-bold uppercase tracking-wider ${style.label}`}>
          {box.label}
        </span>
      </div>
      <h4 className={`text-base font-bold ${style.title} mb-3`}>
        {box.title}
      </h4>
      <ul className="space-y-2">
        {box.items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
            <i className={`ri-arrow-right-s-line mt-0.5 ${style.bullet} flex-shrink-0`}></i>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const stepColorClasses = {
  cyan: {
    gradient: 'from-cyan-500 to-teal-500',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    text: 'text-cyan-600',
    activeBg: 'bg-cyan-500',
    line: 'bg-cyan-300',
  },
  teal: {
    gradient: 'from-teal-500 to-emerald-500',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-600',
    activeBg: 'bg-teal-500',
    line: 'bg-teal-300',
  },
  blue: {
    gradient: 'from-blue-500 to-indigo-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-600',
    activeBg: 'bg-blue-500',
    line: 'bg-blue-300',
  },
  purple: {
    gradient: 'from-purple-500 to-pink-500',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-600',
    activeBg: 'bg-purple-500',
    line: 'bg-purple-300',
  },
  amber: {
    gradient: 'from-amber-500 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-600',
    activeBg: 'bg-amber-500',
    line: 'bg-amber-300',
  },
};

function StepGuideComponent({ guide }: { guide: StepGuide }) {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const colors = stepColorClasses[guide.color] || stepColorClasses.cyan;

  return (
    <div className={`my-8 ${colors.bg} rounded-xl border ${colors.border} overflow-hidden`}>
      <div className={`px-6 py-4 bg-gradient-to-r ${colors.gradient} text-white`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
            <i className="ri-route-line text-xl"></i>
          </div>
          <div>
            <h3 className="font-bold text-lg">{guide.title}</h3>
            <p className="text-white/90 text-sm">{guide.steps.length}ステップで完了</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* デスクトップ版：横並びフロー */}
        <div className="hidden md:block">
          <div className="flex items-start justify-between relative">
            <div className="absolute top-12 left-0 right-0 h-1 flex items-center px-16">
              <div className={`flex-1 h-0.5 ${colors.line}`}></div>
            </div>

            {guide.steps.map((step) => (
              <div key={step.number} className="flex-1 relative z-10">
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setActiveStep(activeStep === step.number ? null : step.number)}
                    className={`w-24 h-24 flex items-center justify-center rounded-full transition-all cursor-pointer ${
                      activeStep === step.number
                        ? `${colors.activeBg} text-white shadow-lg scale-110`
                        : 'bg-white text-gray-400 border-2 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <i className={`${step.icon} text-2xl block mb-1`}></i>
                      <span className="text-xs font-bold">STEP {step.number}</span>
                    </div>
                  </button>

                  <div className="mt-4 text-center px-2">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">{step.title}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{step.description}</p>
                  </div>

                  {activeStep === step.number && step.tips && (
                    <div className={`mt-4 p-3 ${colors.bg} border ${colors.border} rounded-lg text-left w-full max-w-xs`}>
                      <div className="flex items-start gap-2">
                        <div className={`w-5 h-5 flex items-center justify-center ${colors.text} flex-shrink-0 mt-0.5`}>
                          <i className="ri-lightbulb-line text-sm"></i>
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed">{step.tips}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* モバイル版：縦並びフロー */}
        <div className="md:hidden space-y-4">
          {guide.steps.map((step, index) => (
            <div key={step.number}>
              <button
                onClick={() => setActiveStep(activeStep === step.number ? null : step.number)}
                className={`w-full p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  activeStep === step.number
                    ? `${colors.border} ${colors.bg}`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 flex items-center justify-center rounded-full flex-shrink-0 ${
                    activeStep === step.number
                      ? `${colors.activeBg} text-white`
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    <div className="text-center">
                      <i className={`${step.icon} text-xl block mb-0.5`}></i>
                      <span className="text-xs font-bold">STEP {step.number}</span>
                    </div>
                  </div>

                  <div className="flex-1 text-left">
                    <h4 className="font-bold text-gray-900 mb-1">{step.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>

                  <div className={`w-6 h-6 flex items-center justify-center text-gray-400 transition-transform ${
                    activeStep === step.number ? 'rotate-180' : ''
                  }`}>
                    <i className="ri-arrow-down-s-line text-xl"></i>
                  </div>
                </div>

                {activeStep === step.number && step.tips && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-start gap-2">
                      <div className={`w-5 h-5 flex items-center justify-center ${colors.text} flex-shrink-0 mt-0.5`}>
                        <i className="ri-lightbulb-line"></i>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{step.tips}</p>
                    </div>
                  </div>
                )}
              </button>

              {index < guide.steps.length - 1 && (
                <div className="flex justify-center py-2">
                  <div className={`w-0.5 h-6 ${colors.line}`}></div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={`mt-6 p-4 bg-white border ${colors.border} rounded-lg`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center ${colors.activeBg} text-white rounded-full`}>
              <i className="ri-check-double-line text-xl"></i>
            </div>
            <div>
              <p className="font-semibold text-gray-900">すべてのステップを完了すると</p>
              <p className="text-sm text-gray-600">問題が解決し、安定した配信が可能になります</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// フローチャートデータ
const flowchartData: Record<string, DiagnosticFlowchart> = {
  video: {
    category: 'video',
    title: '映像が映らない',
    icon: 'ri-camera-off-line',
    color: 'from-rose-500 to-pink-500',
    nodes: [
      { id: 'v1', type: 'question', text: 'カメラは物理的に接続されていますか？', yesNext: 'v2', noNext: 'v1-sol' },
      { id: 'v1-sol', type: 'solution', text: 'カメラをUSBポートまたはHDMIに接続してください。別のポートも試してみてください。', icon: 'ri-usb-line' },
      { id: 'v2', type: 'question', text: 'デバイスマネージャーでカメラが認識されていますか？', yesNext: 'v3', noNext: 'v2-sol' },
      { id: 'v2-sol', type: 'solution', text: 'カメラドライバーを再インストールしてください。PCを再起動後、再度確認してください。', icon: 'ri-install-line' },
      { id: 'v3', type: 'question', text: '配信ソフトでカメラを選択できますか？', yesNext: 'v4', noNext: 'v3-sol' },
      { id: 'v3-sol', type: 'solution', text: '他のアプリがカメラを使用していないか確認してください。配信ソフトを再起動してください。', icon: 'ri-restart-line' },
      { id: 'v4', type: 'question', text: '映像は正常に表示されていますか？', yesNext: 'v4-ok', noNext: 'v4-sol' },
      { id: 'v4-sol', type: 'solution', text: '解像度・フレームレート設定を下げてみてください。カメラの設定をリセットしてください。', icon: 'ri-settings-3-line' },
      { id: 'v4-ok', type: 'action', text: '映像系統は正常です！音声やネットワークの診断も行ってください。', icon: 'ri-check-double-line' },
    ],
  },
  audio: {
    category: 'audio',
    title: '音声が出ない',
    icon: 'ri-volume-mute-line',
    color: 'from-amber-500 to-orange-500',
    nodes: [
      { id: 'a1', type: 'question', text: 'マイクは物理的に接続されていますか？', yesNext: 'a2', noNext: 'a1-sol' },
      { id: 'a1-sol', type: 'solution', text: 'マイクを正しいポート（USB/オーディオジャック）に接続してください。', icon: 'ri-mic-line' },
      { id: 'a2', type: 'question', text: 'システム設定でマイクが認識されていますか？', yesNext: 'a3', noNext: 'a2-sol' },
      { id: 'a2-sol', type: 'solution', text: 'サウンド設定を開き、入力デバイスを確認してください。ドライバーの更新も試してください。', icon: 'ri-sound-module-line' },
      { id: 'a3', type: 'question', text: 'マイクはミュートになっていませんか？', yesNext: 'a4', noNext: 'a3-sol' },
      { id: 'a3-sol', type: 'solution', text: 'マイク本体のミュートスイッチ、システム設定、配信ソフトのミュート状態を確認してください。', icon: 'ri-volume-mute-line' },
      { id: 'a4', type: 'question', text: '音声レベルは適切ですか（-12dB〜-6dB）？', yesNext: 'a4-ok', noNext: 'a4-sol' },
      { id: 'a4-sol', type: 'solution', text: 'マイクゲインを調整してください。コンプレッサーやリミッターの使用も検討してください。', icon: 'ri-equalizer-line' },
      { id: 'a4-ok', type: 'action', text: '音声系統は正常です！配信テストで最終確認を行ってください。', icon: 'ri-check-double-line' },
    ],
  },
  network: {
    category: 'network',
    title: '接続できない',
    icon: 'ri-wifi-off-line',
    color: 'from-cyan-500 to-teal-500',
    nodes: [
      { id: 'n1', type: 'question', text: 'インターネットに接続されていますか？', yesNext: 'n2', noNext: 'n1-sol' },
      { id: 'n1-sol', type: 'solution', text: 'ルーターを再起動してください。LANケーブルの接続を確認してください。', icon: 'ri-router-line' },
      { id: 'n2', type: 'question', text: '上り速度は10Mbps以上ありますか？', yesNext: 'n3', noNext: 'n2-sol' },
      { id: 'n2-sol', type: 'solution', text: '有線LAN接続に切り替えてください。他のデバイスの通信を制限してください。', icon: 'ri-speed-line' },
      { id: 'n3', type: 'question', text: 'VPNやプロキシを使用していませんか？', yesNext: 'n4', noNext: 'n3-sol' },
      { id: 'n3-sol', type: 'solution', text: 'VPN/プロキシを無効にして配信してください。ファイアウォール設定も確認してください。', icon: 'ri-shield-cross-line' },
      { id: 'n4', type: 'question', text: '配信プラットフォームに接続できますか？', yesNext: 'n4-ok', noNext: 'n4-sol' },
      { id: 'n4-sol', type: 'solution', text: 'ストリームキーとサーバーURLを再確認してください。別のサーバーを試してください。', icon: 'ri-server-line' },
      { id: 'n4-ok', type: 'action', text: 'ネットワークは正常です！ビットレートを適切に設定して配信を開始してください。', icon: 'ri-check-double-line' },
    ],
  },
};

// フローチャートコンポーネント
function DiagnosticFlowchartComponent({ flowchart }: { flowchart: DiagnosticFlowchart }) {
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(flowchart.nodes[0].id);
  const [history, setHistory] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);

  const resetDiagnosis = () => {
    setCurrentNodeId(flowchart.nodes[0].id);
    setHistory([]);
  };

  const handleAnswer = (answer: 'yes' | 'no') => {
    if (!currentNodeId) return;
    
    const currentNode = flowchart.nodes.find(n => n.id === currentNodeId);
    if (!currentNode) return;
    
    const nextId = answer === 'yes' ? currentNode.yesNext : currentNode.noNext;
    
    if (nextId) {
      setHistory([...history, currentNodeId]);
      setCurrentNodeId(nextId);
    }
  };

  const handleOptionSelect = (nextId: string) => {
    if (!currentNodeId) return;
    
    setHistory([...history, currentNodeId]);
    setCurrentNodeId(nextId);
  };

  const goBack = () => {
    if (history.length > 0) {
      const newHistory = [...history];
      const previousId = newHistory.pop();
      setHistory(newHistory);
      setCurrentNodeId(previousId || null);
    } else {
      resetDiagnosis();
    }
  };

  const currentNode = flowchart.nodes.find(n => n.id === currentNodeId);

  return (
    <div className="my-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-700 to-gray-800 text-white cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
            <i className="ri-git-branch-line text-xl"></i>
          </div>
          <div className="text-left">
            <h3 className="font-bold text-lg">トラブル診断フローチャート</h3>
            <p className="text-gray-300 text-sm">{flowchart.title}の診断</p>
          </div>
        </div>
        <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-xl"></i>
        </div>
      </button>

      <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[1000px]' : 'max-h-0'}`}>
        <div className="p-6">
          {/* ヘッダー */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${flowchart.color} text-white`}>
                <i className={flowchart.icon}></i>
              </div>
              <span className="font-semibold text-gray-900">{flowchart.title}の診断</span>
            </div>
            <button
              onClick={resetDiagnosis}
              className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-1"
            >
              <i className="ri-refresh-line"></i>
              最初から
            </button>
          </div>

          {/* 進捗インジケーター */}
          <div className="flex items-center gap-2 mb-6">
            {flowchart.nodes.filter(n => n.type === 'question').map((_, index) => (
              <div
                key={index}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  index <= history.length ? 'bg-cyan-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* 現在のノード */}
          {currentNode && (
            <div className={`p-6 rounded-xl ${
              currentNode.type === 'question' 
                ? 'bg-white border-2 border-cyan-200' 
                : currentNode.type === 'solution'
                ? 'bg-amber-50 border-2 border-amber-200'
                : 'bg-teal-50 border-2 border-teal-200'
            }`}>
              {currentNode.type === 'question' ? (
                <>
                  <div className="flex items-start gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center bg-cyan-100 text-cyan-600 rounded-full flex-shrink-0">
                      <i className="ri-question-line text-xl"></i>
                    </div>
                    <p className="text-lg font-medium text-gray-900 pt-1">{currentNode.text}</p>
                  </div>
                  
                  {/* 複数選択肢がある場合 */}
                  {currentNode.options && currentNode.options.length > 0 ? (
                    <div className="space-y-3">
                      {currentNode.options.map((option, index) => (
                        <button
                          key={index}
                          onClick={() => handleOptionSelect(option.next)}
                          className="w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-lg hover:from-cyan-600 hover:to-teal-600 transition-all cursor-pointer font-medium flex items-center justify-between gap-2 whitespace-nowrap"
                        >
                          <span>{option.label}</span>
                          <i className="ri-arrow-right-line"></i>
                        </button>
                      ))}
                    </div>
                  ) : (
                    /* はい/いいえの2択 */
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleAnswer('yes')}
                        className="flex-1 py-3 px-6 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-check-line"></i>
                        はい
                      </button>
                      <button
                        onClick={() => handleAnswer('no')}
                        className="flex-1 py-3 px-6 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors cursor-pointer font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                      >
                        <i className="ri-close-line"></i>
                        いいえ
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${
                      currentNode.type === 'solution' 
                        ? 'bg-amber-100 text-amber-600' 
                        : 'bg-teal-100 text-teal-600'
                    }`}>
                      <i className={`${currentNode.icon || 'ri-lightbulb-line'} text-xl`}></i>
                    </div>
                    <div>
                      <span className={`text-sm font-medium ${
                        currentNode.type === 'solution' ? 'text-amber-600' : 'text-teal-600'
                      }`}>
                        {currentNode.type === 'solution' ? '解決策' : '診断完了'}
                      </span>
                      <p className="text-gray-900 mt-1">{currentNode.text}</p>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={goBack}
                      className="flex-1 py-3 px-6 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line"></i>
                      戻る
                    </button>
                    <button
                      onClick={resetDiagnosis}
                      className="flex-1 py-3 px-6 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors cursor-pointer font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-refresh-line"></i>
                      別の診断
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* 戻るボタン（質問中のみ） */}
          {currentNode?.type === 'question' && history.length > 0 && (
            <button
              onClick={goBack}
              className="mt-4 text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-1 text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              前の質問に戻る
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// テンプレートからボックスをパースする関数
function parseBoxFromTemplate(html: string, type: 'highlight' | 'warning' | 'tip' | 'checklist'): InfoBox | null {
  const titleMatch = html.match(/<h4[^>]*>(.*?)<\/h4>/s);
  const listItemsMatch = html.match(/<li[^>]*>(.*?)<\/li>/gs);
  
  if (!titleMatch) return null;
  
  const title = titleMatch[1].replace(/<[^>]*>/g, '').trim();
  const items = listItemsMatch 
    ? listItemsMatch.map(li => li.replace(/<\/?li[^>]*>/g, '').replace(/<[^>]*>/g, '').trim())
    : [];

  return {
    type,
    icon: boxIcons[type],
    label: boxLabels[type],
    title,
    items,
  };
}

// テンプレートからステップガイドをパースする関数
function parseStepGuideFromTemplate(html: string): StepGuide | null {
  const containerMatch = html.match(/<div[^>]*class="step-guide"[^>]*data-color="([^"]*)"[^>]*data-title="([^"]*)"[^>]*>/);
  if (!containerMatch) {
    // 別の形式も試す
    const altMatch = html.match(/data-title="([^"]*)"[^>]*data-color="([^"]*)"/);
    if (!altMatch) return null;
  }
  
  const colorMatch = html.match(/data-color="([^"]*)"/);
  const titleMatch = html.match(/data-title="([^"]*)"/);
  
  const color = (colorMatch?.[1] || 'cyan') as StepGuide['color'];
  const title = titleMatch?.[1] || 'ステップガイド';
  
  const stepMatches = html.match(/<div[^>]*class="step"[^>]*>[\s\S]*?<\/div>\s*(?=<div[^>]*class="step"|<\/div>\s*<!--)/g);
  
  if (!stepMatches || stepMatches.length === 0) {
    // 別のパターンで試す
    const stepRegex = /<div[^>]*class="step"[^>]*data-icon="([^"]*)"[^>]*>[\s\S]*?<h4[^>]*>(.*?)<\/h4>[\s\S]*?<p[^>]*>(.*?)<\/p>[\s\S]*?(?:<span[^>]*class="step-tips"[^>]*>(.*?)<\/span>)?/g;
    const steps: Step[] = [];
    let match;
    let stepNumber = 1;
    
    while ((match = stepRegex.exec(html)) !== null) {
      steps.push({
        number: stepNumber++,
        icon: match[1] || `ri-number-${stepNumber - 1}`,
        title: match[2].replace(/<[^>]*>/g, '').trim(),
        description: match[3].replace(/<[^>]*>/g, '').trim(),
        tips: match[4]?.replace(/<[^>]*>/g, '').trim(),
      });
    }
    
    if (steps.length > 0) {
      return { title, color, steps };
    }
    return null;
  }
  
  const steps: Step[] = stepMatches.map((stepHtml, index) => {
    const iconMatch = stepHtml.match(/data-icon="([^"]*)"/);
    const stepTitleMatch = stepHtml.match(/<h4[^>]*>(.*?)<\/h4>/s);
    const descMatch = stepHtml.match(/<p[^>]*>(.*?)<\/p>/s);
    const tipsMatch = stepHtml.match(/<span[^>]*class="step-tips"[^>]*>(.*?)<\/span>/s);
    
    return {
      number: index + 1,
      icon: iconMatch?.[1] || `ri-number-${index + 1}`,
      title: stepTitleMatch?.[1]?.replace(/<[^>]*>/g, '').trim() || `ステップ ${index + 1}`,
      description: descMatch?.[1]?.replace(/<[^>]*>/g, '').trim() || '',
      tips: tipsMatch?.[1]?.replace(/<[^>]*>/g, '').trim(),
    };
  });
  
  return { title, color, steps };
}

// テンプレートからフローチャートをパースする関数
function parseFlowchartFromTemplate(match: string): DiagnosticFlowchart | null {
  // カスタムフローチャートのJSONをパース
  const customMatch = match.match(/<!--\s*flowchart:custom\s*-->([\s\S]*?)<!--\s*\/flowchart\s*-->/);
  if (customMatch) {
    try {
      const jsonStr = customMatch[1].trim();
      const customData = JSON.parse(jsonStr);
      return {
        category: 'custom',
        title: customData.title || 'カスタム診断',
        icon: customData.icon || 'ri-question-line',
        color: customData.color || 'from-gray-500 to-gray-600',
        nodes: customData.nodes || [],
      };
    } catch (e) {
      console.error('Failed to parse custom flowchart JSON:', e);
      return null;
    }
  }
  
  // プリセットフローチャート
  const categoryMatch = match.match(/<!--\s*flowchart:(video|audio|network)\s*-->/);
  if (categoryMatch) {
    const categoryId = categoryMatch[1];
    return flowchartData[categoryId] || null;
  }
  
  return null;
}

type ContentSegment = 
  | { type: 'html'; content: string }
  | { type: 'box'; box: InfoBox; key: number }
  | { type: 'stepGuide'; guide: StepGuide; key: number }
  | { type: 'flowchart'; flowchart: DiagnosticFlowchart; key: number };

export default function ContentRenderer({ content, slug }: ContentRendererProps) {
  const renderedContent = useMemo(() => {
    const segments: ContentSegment[] = [];
    let remainingContent = content;
    let boxKey = 0;
    let stepKey = 0;
    let flowchartKey = 0;
    
    // ボックステンプレートのパターン
    const boxPattern = /<!--\s*box:(highlight|warning|tip|checklist)\s*-->([\s\S]*?)<!--\s*\/box:\1\s*-->/g;
    // ステップテンプレートのパターン
    const stepsPattern = /<!--\s*steps\s*-->([\s\S]*?)<!--\s*\/steps\s*-->/g;
    // フローチャートテンプレートのパターン（カスタムとプリセット両方に対応）
    const flowchartPattern = /<!--\s*flowchart:(video|audio|network|custom)\s*-->[\s\S]*?<!--\s*\/flowchart\s*-->/g;
    
    // すべてのテンプレートを検出して位置でソート
    const allMatches: Array<{
      index: number;
      length: number;
      type: 'box' | 'steps' | 'flowchart';
      boxType?: 'highlight' | 'warning' | 'tip' | 'checklist';
      html: string;
    }> = [];
    
    let match;
    
    // ボックスを検出
    while ((match = boxPattern.exec(content)) !== null) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        type: 'box',
        boxType: match[1] as 'highlight' | 'warning' | 'tip' | 'checklist',
        html: match[2],
      });
    }
    
    // ステップを検出
    while ((match = stepsPattern.exec(content)) !== null) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        type: 'steps',
        html: match[1],
      });
    }
    
    // フローチャートを検出
    while ((match = flowchartPattern.exec(content)) !== null) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        type: 'flowchart',
        html: match[0],
      });
    }
    
    // テンプレートがない場合はそのまま返す
    if (allMatches.length === 0) {
      return [{ type: 'html', content }];
    }
    
    // 位置でソート
    allMatches.sort((a, b) => a.index - b.index);
    
    let lastIndex = 0;
    
    for (const templateMatch of allMatches) {
      // テンプレート前のHTMLを追加
      if (templateMatch.index > lastIndex) {
        const htmlBefore = content.slice(lastIndex, templateMatch.index);
        if (htmlBefore.trim()) {
          segments.push({ type: 'html', content: htmlBefore });
        }
      }
      
      // テンプレートをパースして追加
      if (templateMatch.type === 'box' && templateMatch.boxType) {
        const box = parseBoxFromTemplate(templateMatch.html, templateMatch.boxType);
        if (box) {
          segments.push({ type: 'box', box, key: boxKey++ });
        }
      } else if (templateMatch.type === 'steps') {
        const guide = parseStepGuideFromTemplate(templateMatch.html);
        if (guide) {
          segments.push({ type: 'stepGuide', guide, key: stepKey++ });
        }
      } else if (templateMatch.type === 'flowchart') {
        const flowchart = parseFlowchartFromTemplate(templateMatch.html);
        if (flowchart) {
          segments.push({ type: 'flowchart', flowchart, key: flowchartKey++ });
        }
      }
      
      lastIndex = templateMatch.index + templateMatch.length;
    }
    
    // 残りのHTMLを追加
    if (lastIndex < content.length) {
      const htmlAfter = content.slice(lastIndex);
      if (htmlAfter.trim()) {
        segments.push({ type: 'html', content: htmlAfter });
      }
    }
    
    return segments;
  }, [content]);

  return (
    <div className="article-content">
      <style>{`
        /* ===== 全体設定 ===== */
        .article-content {
          font-size: 15.5px !important;
          line-height: 2 !important;
          color: #333 !important;
          letter-spacing: 0.02em !important;
        }

        /* ===== H2 見出し ===== */
        .article-content h2,
        .article-content div h2 {
          font-size: 1.45rem !important;
          font-weight: 800 !important;
          color: #1a1a1a !important;
          margin-top: 3.5rem !important;
          margin-bottom: 1.5rem !important;
          padding-bottom: 0.85rem !important;
          padding-left: 0 !important;
          padding-top: 0 !important;
          border-bottom: 3px solid #222 !important;
          border-left: none !important;
          background: none !important;
          border-radius: 0 !important;
          position: relative !important;
          line-height: 1.5 !important;
        }
        .article-content h2::before,
        .article-content div h2::before {
          display: none !important;
        }

        /* ===== H3 見出し ===== */
        .article-content h3,
        .article-content div h3 {
          font-size: 1.2rem !important;
          font-weight: 700 !important;
          color: #1a1a1a !important;
          margin-top: 2.8rem !important;
          margin-bottom: 1.2rem !important;
          padding: 0.7rem 0 0.7rem 2.8rem !important;
          border-left: none !important;
          border-bottom: 1px solid #e5e5e5 !important;
          background: none !important;
          border-radius: 0 !important;
          position: relative !important;
          line-height: 1.5 !important;
          counter-increment: h3-counter !important;
        }
        .article-content {
          counter-reset: h3-counter !important;
        }
        .article-content h3::before,
        .article-content div h3::before {
          content: counter(h3-counter) !important;
          position: absolute !important;
          left: 0 !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          width: 2rem !important;
          height: 2rem !important;
          background: #f5c518 !important;
          color: #fff !important;
          border-radius: 50% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 0.85rem !important;
          font-weight: 800 !important;
          line-height: 1 !important;
        }

        /* ===== H4 見出し ===== */
        .article-content h4,
        .article-content div h4 {
          font-size: 1.05rem !important;
          font-weight: 700 !important;
          color: #333 !important;
          margin-top: 2rem !important;
          margin-bottom: 0.8rem !important;
          padding-left: 0.85rem !important;
          border-left: 3px solid #f5c518 !important;
          background: none !important;
          line-height: 1.5 !important;
        }

        /* ===== 段落 ===== */
        .article-content p,
        .article-content div p {
          color: #444 !important;
          line-height: 2.05 !important;
          margin-bottom: 1.6rem !important;
          font-size: 15.5px !important;
          letter-spacing: 0.02em !important;
        }

        /* ===== 強調テキスト（黄色マーカー風） ===== */
        .article-content strong,
        .article-content div strong {
          color: #1a1a1a !important;
          font-weight: 700 !important;
          background: linear-gradient(transparent 60%, #fff3a8 60%) !important;
          padding: 0 2px !important;
        }

        /* ===== リスト ===== */
        .article-content ul,
        .article-content div ul {
          margin: 1.2rem 0 1.8rem !important;
          padding-left: 1.5rem !important;
          list-style: disc !important;
        }
        .article-content ol,
        .article-content div ol {
          margin: 1.2rem 0 1.8rem !important;
          padding-left: 1.5rem !important;
          list-style: decimal !important;
        }
        .article-content li,
        .article-content div li {
          color: #444 !important;
          margin-bottom: 0.6rem !important;
          line-height: 1.9 !important;
          font-size: 15px !important;
        }

        /* ===== リンク ===== */
        .article-content a,
        .article-content div a {
          color: #0891b2 !important;
          text-decoration: underline !important;
          text-underline-offset: 3px !important;
        }
        .article-content a:hover,
        .article-content div a:hover {
          color: #06b6d4 !important;
        }

        /* ===== 画像 ===== */
        .article-content img,
        .article-content div img {
          border-radius: 0.5rem !important;
          margin: 2rem 0 !important;
          max-width: 100% !important;
        }

        /* ===== 引用 ===== */
        .article-content blockquote,
        .article-content div blockquote {
          border-left: 4px solid #e5e5e5 !important;
          padding: 1rem 1.2rem !important;
          margin: 1.5rem 0 !important;
          background: #fafafa !important;
          border-radius: 0 0.5rem 0.5rem 0 !important;
          color: #666 !important;
          font-style: italic !important;
        }

        /* ===== テーブル ===== */
        .article-content table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: 1.5rem 0 !important;
          font-size: 14px !important;
        }
        .article-content th {
          background: #f5f5f5 !important;
          font-weight: 700 !important;
          padding: 0.75rem 1rem !important;
          border: 1px solid #e5e5e5 !important;
          text-align: left !important;
        }
        .article-content td {
          padding: 0.75rem 1rem !important;
          border: 1px solid #e5e5e5 !important;
        }

        /* ===== コードブロック ===== */
        .article-content code {
          background: #f5f5f5 !important;
          padding: 0.15rem 0.4rem !important;
          border-radius: 0.25rem !important;
          font-size: 0.9em !important;
          color: #e11d48 !important;
        }
        .article-content pre {
          background: #1e1e1e !important;
          color: #d4d4d4 !important;
          padding: 1.2rem !important;
          border-radius: 0.5rem !important;
          overflow-x: auto !important;
          margin: 1.5rem 0 !important;
          font-size: 13px !important;
          line-height: 1.7 !important;
        }
        .article-content pre code {
          background: none !important;
          color: inherit !important;
          padding: 0 !important;
        }

        /* ===== 水平線 ===== */
        .article-content hr {
          border: none !important;
          border-top: 1px solid #e5e5e5 !important;
          margin: 2.5rem 0 !important;
        }
      `}</style>
      {renderedContent.map((segment, idx) => {
        if (segment.type === 'html') {
          return (
            <div
              key={`html-${idx}`}
              dangerouslySetInnerHTML={{ __html: segment.content.replace(/\n/g, '<br />') }}
            />
          );
        }
        if (segment.type === 'box') {
          return <InfoBoxComponent key={`box-${segment.key}`} box={segment.box} />;
        }
        if (segment.type === 'stepGuide') {
          return <StepGuideComponent key={`step-${segment.key}`} guide={segment.guide} />;
        }
        if (segment.type === 'flowchart') {
          return <DiagnosticFlowchartComponent key={`flowchart-${segment.key}`} flowchart={segment.flowchart} />;
        }
        return null;
      })}
    </div>
  );
}
